import Grid from '@mui/material/Grid'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import { getDateTimePickerRangesFromPermissions } from 'helpers/settingsModal.helper/settingsModal.helper'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Step } from 'react-joyride'
import { Card } from 'ui/atoms'
import ModuleHeader from 'ui/components/ModuleHeader/ModuleHeader'
import SettingsTable, { SettingsPermissions } from 'ui/components/SettingsTable/SettingsTable'
import SystemMessageBanner from 'ui/components/SystemMessageBanner/SystemMessageBanner'
import Tour from 'ui/components/Tour/Tour'
import View from 'ui/components/View/View'

const OptimizeModelSettingHeaderContentClass = 'optSettingsHeader'
const widgetConstants = {
  OPTIMIZE_MODEL_SETTINGS_TABLE: 'optimize-model-settings-table',
  OPTIMIZE_SETTINGS_PRICES: 'optimize-settings-prices',
}

const modelInfo: Array<Step> = [
  {
    target: widgetConstants.OPTIMIZE_MODEL_SETTINGS_TABLE,
    content:
      "Here you can find all active base values for the plants' optimization model. \n\nA base value is an input to the optimization model and does not categorize as a deviation. These settings are used to change the base value for a property, ie. which value is used when there are no deviation settings currently active. Click on 'Show/edit' to change base value, edit and delete current settings.\n\nNote that base values affect the production plan for the entire system!",
    title: 'Table showing all base values',
  },
]

const priceInfo: Array<Step> = [
  {
    target: widgetConstants.OPTIMIZE_SETTINGS_PRICES,
    content:
      'All prices and cost inputs to the optimization model are shown here. These properties have the same functionality as base values.',
    title: 'Opt model prices',
  },
]

const steps: Array<Step> = [...modelInfo, ...priceInfo]

export default function OptimizeModelSettingsView(): ReactElement {
  const { t } = useTranslation()

  const [runTour, setRunTour] = useState(false)

  const permissions: SettingsPermissions = {
    canEdit: useHasPermission('change_model_settings'),
    canEditHistoric: useHasPermission('change_historic_model_settings'),
  }

  const startEndTimeRange = getDateTimePickerRangesFromPermissions(permissions.canEditHistoric)

  return (
    <View
      header={
        <div className={OptimizeModelSettingHeaderContentClass}>
          <ModuleHeader
            infoHeader={t('Opt model settings')}
            onInfoButtonClick={() => {
              setRunTour(true)
            }}
          />
        </div>
      }
    >
      <SystemMessageBanner module="optimizemodelsettings" />
      <Tour isSingle={false} run={runTour} setRunState={setRunTour} steps={steps} />

      <Grid size={{ xs: 12 }}>
        <Card tourId={widgetConstants.OPTIMIZE_MODEL_SETTINGS_TABLE} title={t('Optimization settings - base values')}>
          <SettingsTable
            permissions={permissions}
            startEndTimeRange={startEndTimeRange}
            classification={'base'}
            tableOptions={{
              initialState: { sortBy: [{ id: 'updatedAt', desc: true }], hiddenColumns: ['category', 'status'] },
            }}
          />
        </Card>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Card tourId={widgetConstants.OPTIMIZE_SETTINGS_PRICES} title={t('Prices')}>
          <SettingsTable
            permissions={permissions}
            startEndTimeRange={startEndTimeRange}
            classification={'price'}
            tableOptions={{
              initialState: { sortBy: [{ id: 'updatedAt', desc: true }], hiddenColumns: ['category', 'status'] },
            }}
          />
        </Card>
      </Grid>
    </View>
  )
}
